// The grid's arithmetic, kept pure: minutes on the day's axis, the
// window the day needs, and the lanes overlapping cells share inside
// a column (group sessions and deliberate overlaps exist — day-view.md
// §6, §7). The component only positions what comes out of here.

export const toMin = (hhmm: string): number => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

export const fromMin = (min: number): string =>
  `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;

// The gutter's label: the hour alone, "09" (Onur, 14.10.2026). A day
// configured to start off the hour keeps its minutes.
export const hourLabel = (min: number): string =>
  min % 60 === 0 ? fromMin(min).slice(0, 2) : fromMin(min);

export interface Span {
  startMin: number;
  endMin: number; // > startMin
}

export interface Window {
  fromMin: number; // whole hour
  toMin: number; // whole hour, > fromMin
}

// The configured hours, widened to whole hours so that every span on
// the day is inside — an 08:30 seans is shown, not hidden.
export function gridWindow(
  hours: {from: string; to: string}, spans: Span[]
): Window {
  let from = toMin(hours.from);
  let to = toMin(hours.to);
  for (const s of spans) {
    from = Math.min(from, Math.floor(s.startMin / 60) * 60);
    to = Math.max(to, Math.ceil(s.endMin / 60) * 60);
  }
  if (to <= from) to = from + 60;
  return {fromMin: from, toMin: to};
}

export interface Placed<T> extends Span {
  item: T;
  lane: number; // 0-based column inside the provider's column
  lanes: number; // how many lanes this cell's cluster needs
}

// Lanes the way calendars do it: sort by start (longer first on a
// tie), walk the day, and keep a cluster open while cells still touch
// it; inside a cluster each cell takes the first lane that is free at
// its start, and every member is told the cluster's width.
export function assignLanes<T>(spans: (Span & {item: T})[]): Placed<T>[] {
  const sorted = [...spans].sort((a, b) =>
    a.startMin - b.startMin || (b.endMin - b.startMin) - (a.endMin - a.startMin));
  const out: Placed<T>[] = [];
  let cluster: Placed<T>[] = [];
  let laneEnds: number[] = []; // per lane, when its last cell ends
  let clusterEnd = -Infinity;

  const close = () => {
    const lanes = laneEnds.length || 1;
    for (const p of cluster) p.lanes = lanes;
    cluster = [];
    laneEnds = [];
  };

  for (const s of sorted) {
    if (s.startMin >= clusterEnd) close();
    let lane = laneEnds.findIndex((end) => end <= s.startMin);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(s.endMin);
    } else {
      laneEnds[lane] = s.endMin;
    }
    const placed: Placed<T> = {...s, lane, lanes: 1};
    cluster.push(placed);
    out.push(placed);
    clusterEnd = Math.max(clusterEnd, s.endMin);
  }
  close();
  return out;
}

// A cell's vertical place as fractions of an hour from the window's
// top — the component multiplies by the hour height.
export function hourOffset(w: Window, min: number): number {
  return (min - w.fromMin) / 60;
}

export const DEFAULT_SPAN_MIN = 50; // a cancelled record with no end time

// Only the danışmanlar with something on the day get a column — an
// empty column says nothing a header would not (Onur, 02.10.2026). A
// calendar that could not be read is not an empty day: it stays,
// hatched, so nobody takes a failed read for a free day.
export function shownProviders<P extends {providerId: string; calendarOk: boolean}>(
  providers: P[], busy: Iterable<string>,
): P[] {
  const has = new Set(busy);
  return providers.filter((p) => !p.calendarOk || has.has(p.providerId));
}

export const SNAP_MIN = 5; // a dragged cell lands on five-minute marks

// Where a dragged cell lands: its start moved by `deltaMin`, snapped
// to the step, kept inside the window with its whole duration.
export function snapStart(
  startMin: number, durationMin: number, deltaMin: number, w: Window,
  step = SNAP_MIN,
): number {
  const snapped = Math.round((startMin + deltaMin) / step) * step;
  const latest = w.toMin - durationMin;
  return Math.max(w.fromMin, Math.min(latest, snapped));
}

// The other spans of a column a slot would share time with — the
// grid's preview of the warning the use-case gives after the move.
export function overlapsAt<T>(
  spans: (Span & {item: T})[], startMin: number, endMin: number,
): T[] {
  return spans
    .filter((s) => s.startMin < endMin && s.endMin > startMin)
    .map((s) => s.item);
}
